<div class="invoice-preview" [class.dialog-mode]="isDialogMode">
  <div class="preview-shell">
    <div class="preview-header">
      <div class="header-copy">
        <p class="eyebrow">Purchase Order</p>
        <div class="header-title-row">
          <h2>Procurement Print Preview</h2>
          <div class="toolbar" aria-label="Purchase order actions">
            <mat-slide-toggle
              class="sign-toggle"
              color="primary"
              [checked]="includeSignature"
              [disabled]="isGenerating"
              (change)="onSignatureToggle($event.checked)">
              Sign + Stamp
            </mat-slide-toggle>
            <button mat-raised-button color="primary" (click)="printPdf()">
              Print
            </button>
            <button mat-stroked-button color="primary" (click)="downloadPdf()">
              Download PDF
            </button>
            <button mat-flat-button color="accent" (click)="sendMail()">
              Mail Invoice
            </button>

            <button
              *ngIf="isDialogMode"
              mat-icon-button
              type="button"
              class="close-preview-button"
              aria-label="Close preview"
              (click)="closePreview()">
              <mat-icon svgIcon="mat:close"></mat-icon>
            </button>
          </div>
        </div>
        <p class="subtext">
          Purchase-order focused preview with clean vendor summary, item
          costing, and optional sign or stamp.
        </p>
      </div>
    </div>

    <div class="preview-frame" *ngIf="previewData; else loading">
      <div class="invoice-page">
        <div class="invoice-top-strip">
          <span></span>
          <span></span>
        </div>

        <div class="invoice-head">
          <div class="logo-cell">
            <img
              *ngIf="previewData.logo; else logoFallback"
              [src]="previewData.logo"
              alt="Company Logo" />
            <ng-template #logoFallback>
              <div class="logo-fallback">{{ previewData.companyInitials }}</div>
            </ng-template>
          </div>

          <div class="company-cell">
            <div class="document-kicker">Procurement Department</div>
            <h1>{{ previewData.companyName }}</h1>
            <p *ngIf="previewData.companyAddress">
              {{ previewData.companyAddress }}
            </p>
            <p *ngFor="let meta of previewData.companyMeta">{{ meta }}</p>
          </div>

          <div class="invoice-meta-box">
            <div class="meta-badge">PURCHASE ORDER</div>
            <p><strong>PO No:</strong> {{ previewData.poNo }}</p>
            <p><strong>PO Date:</strong> {{ previewData.poDate }}</p>
            <p *ngIf="previewData.status">
              <strong>Status:</strong> {{ previewData.status }}
            </p>
          </div>
        </div>

        <div class="po-overview">
          <div class="overview-card">
            <span class="overview-label">Vendor</span>
            <strong>{{ previewData.vendorName }}</strong>
          </div>
          <div class="overview-card">
            <span class="overview-label">Items</span>
            <strong>{{ previewData.items.length }}</strong>
          </div>
          <div class="overview-card">
            <span class="overview-label">PO Value</span>
            <strong>{{ previewData.netAmount }}</strong>
          </div>
          <div class="overview-card" *ngIf="previewData.status">
            <span class="overview-label">Current Status</span>
            <strong>{{ previewData.status }}</strong>
          </div>
        </div>

        <div class="party-grid">
          <section>
            <h3>Supplier Information</h3>
            <h4>{{ previewData.vendorName }}</h4>
            <p *ngIf="previewData.vendorAddress">
              {{ previewData.vendorAddress }}
            </p>
            <p *ngFor="let line of previewData.vendorMeta">{{ line }}</p>
          </section>

          <section>
            <h3>Bill To</h3>
            <h4 *ngIf="previewData.billingName">
              {{ previewData.billingName }}
            </h4>
            <p *ngIf="previewData.billingAddress">
              {{ previewData.billingAddress }}
            </p>
            <p *ngFor="let line of previewData.billingMeta">{{ line }}</p>
          </section>

          <section>
            <h3>Ship To</h3>
            <h4 *ngIf="previewData.deliveryName">
              {{ previewData.deliveryName }}
            </h4>
            <p *ngIf="previewData.deliveryAddress">
              {{ previewData.deliveryAddress }}
            </p>
            <p *ngFor="let line of previewData.deliveryMeta">{{ line }}</p>
          </section>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th>SR</th>
              <th>DESCRIPTION</th>
              <th>HSN</th>
              <th>UOM</th>
              <th>QTY</th>
              <th>RATE</th>
              <th>DISCOUNT</th>
              <th>TAXABLE</th>
              <th>TAX</th>
              <th>LINE TOTAL</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of previewData.items">
              <td class="center">{{ item.sr }}</td>
              <td>
                <div>{{ item.description }}</div>
                <div class="item-remark" *ngFor="let remark of item.remarkLines">
                  {{ remark }}
                </div>
              </td>
              <td class="center">{{ item.hsn }}</td>
              <td class="center">{{ item.uom }}</td>
              <td class="right">{{ item.qty }}</td>
              <td class="right">{{ item.rate }}</td>
              <td class="right">{{ item.discount }}</td>
              <td class="right">{{ item.taxable }}</td>
              <td class="right">{{ item.tax }}</td>
              <td class="right strong">{{ item.lineTotal }}</td>
            </tr>
            <tr class="grand-row">
              <td colspan="9" class="right">GRAND TOTAL</td>
              <td class="right">{{ previewData.netAmount }}</td>
            </tr>
          </tbody>
        </table>

        <div class="bottom-grid">
          <div class="details-block">
            <section *ngIf="previewData.bankDetails.length">
              <h3>Bank Details</h3>
              <p *ngFor="let line of previewData.bankDetails">{{ line }}</p>
            </section>
            <section class="words-block">
              <h3>Amount In Words</h3>
              <p>{{ previewData.amountInWords }}</p>
            </section>
            <section *ngIf="previewData.paymentTerms">
              <h3>Commercial Terms</h3>
              <p>{{ previewData.paymentTerms }}</p>
            </section>
            <section *ngIf="previewData.remarks !== '-'">
              <h3>Remarks</h3>
              <p>{{ previewData.remarks }}</p>
            </section>
          </div>

          <div class="summary-sign-block">
            <table class="summary-table">
              <tr *ngFor="let row of previewData.summary">
                <td>{{ row.label }}</td>
                <td>{{ row.value }}</td>
              </tr>
            </table>

            <div class="html-sign-area">
              <div class="sign-company">
                For {{ previewData.companyNameTitle }}
              </div>
              <div class="sign-assets" *ngIf="includeSignature">
                <img
                  *ngIf="previewData.stampImage"
                  class="stamp-img"
                  [src]="previewData.stampImage"
                  alt="Company Stamp" />
                <img
                  *ngIf="previewData.signatureImage"
                  class="signature-img"
                  [src]="previewData.signatureImage"
                  alt="Signature" />
              </div>
              <div class="sign-name">{{ previewData.signatoryName }}</div>
              <div class="sign-role" *ngIf="previewData.signatoryTitle">
                {{ previewData.signatoryTitle }}
              </div>
            </div>
          </div>
        </div>

        <div class="invoice-footer">
          <div class="footer-line">
            <span></span>
            <span></span>
          </div>
          <div class="footer-text">
            <span>Generated using VCS ERP</span>
            <span>Page 1 of 1</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template #loading>
    <div class="loading-state">
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      <p class="loading-text">
        {{
          includeSignature
            ? "Generating signed purchase order preview..."
            : "Generating purchase order preview..."
        }}
      </p>
    </div>
  </ng-template>
</div>
